import type { WebOrderJourney, WebOrderPart, WebOrderRow, WebOrderState } from "../../../api/types";
import { BUSINESS_TIME_ZONE } from "../../../utils/businessDay";

/**
 * G-143 — the words and badges of the web-orders report, in one place so
 * the By shop tab, the person page and the journey drawer never disagree.
 */
export const STATE_BADGE: Record<WebOrderState, { label: string; className: string }> = {
  OFFERED: { label: "Offered", className: "badge muted" },
  PREPARING: { label: "Preparing", className: "badge primary" },
  NEEDS_REVIEW: { label: "Needs review", className: "badge warning" },
  HANDED_OVER: { label: "Handed over", className: "badge primary" },
  WITH_COURIER: { label: "With courier", className: "badge primary" },
  DELIVERED: { label: "Delivered", className: "badge success" },
  DELIVERY_FAILED: { label: "Could not deliver", className: "badge danger" },
  CANCELLED: { label: "Cancelled", className: "badge danger" },
  NOT_MADE: { label: "Could not be made", className: "badge warning" },
  SKIPPED: { label: "Skipped", className: "badge muted" },
};

export const PART_LABEL: Record<WebOrderPart, string> = {
  ACCEPTED: "Accepted",
  BUILT: "Built",
  HANDED_OVER: "Handed over",
  COURIER: "Delivered",
};

const whenFormat = new Intl.DateTimeFormat("en-GB", {
  timeZone: BUSINESS_TIME_ZONE,
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** "13 Oct 11:20", on the Barcelona clock the report counts in. */
export function whenText(iso: string): string {
  return whenFormat.format(new Date(iso)).replace(",", "");
}

export function minutesText(minutes: number | null): string {
  return minutes === null ? "—" : `${minutes} min`;
}

export function orderLabel(row: Pick<WebOrderRow, "orderNumber" | "id">): string {
  return row.orderNumber ?? row.id.slice(0, 8);
}

export function linesText(row: Pick<WebOrderRow, "lines">): string {
  return row.lines.map((l) => `${Number(l.quantity)} × ${l.title}`).join(", ");
}

export function namesText(people: readonly { fullName: string }[]): string {
  return people.length ? people.map((p) => p.fullName).join(", ") : "—";
}

/** A share as a whole percent; nothing of nothing is 0 %. */
export function percentOf(part: number, whole: number): number {
  return whole > 0 ? (part / whole) * 100 : 0;
}

type Step = WebOrderJourney["steps"][number];

/** One line of the journey: what happened, and by whom. */
export function stepTitle(step: Step): string {
  const who = step.person?.fullName;
  switch (step.kind) {
    case "RECEIVED":
      return "Received from Shopify";
    case "OFFERED":
      return step.detail ? `Offered to ${step.detail}` : "Offered to the shops";
    case "ACCEPTED":
      return `Accepted by ${who ?? "a shop"}`;
    // G-167 (OD-283): the office gave it to a shop, or moved it to another.
    case "ASSIGNED":
      return `Assigned by ${who ?? "the office"}`;
    case "PASSED":
      return `Passed by ${who ?? "the office"}`;
    case "BUILT":
      return `Bouquet built by ${who ?? "—"}`;
    case "HANDED_OVER":
      return `Handed over by ${who ?? "—"}`;
    case "COURIER_ASSIGNED":
      return who ? `Courier ${who} assigned` : "Courier assigned";
    case "PICKED_UP":
      return `Picked up by ${who ?? "the courier"}`;
    case "DELIVERED":
      return `Delivered by ${who ?? "the courier"}`;
    case "DELIVERY_FAILED":
      return "Could not deliver";
    case "CANCELLED":
      return "Cancelled";
  }
}

/** The small line under a step: its time, plus the place or reason it carries. */
export function stepDetail(step: Step): string {
  const time = whenText(step.at);
  if (!step.detail) return time;
  if (step.kind === "PICKED_UP") return `${time} · confirmed by ${step.detail}`;
  if (step.kind === "OFFERED") return time;
  return `${time} · ${step.detail}`;
}
